import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, AlertTriangle, Shield, Bell, Clock, FileCheck, Zap } from 'lucide-react';

const FireAlarm = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Commercial Fire Alarm Installation & Verification
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional fire alarm system installation, testing, and verification services for commercial buildings in London, Kingston, and Ontario. ECRA/ESA licensed electricians ensuring code-compliant fire safety systems.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Request Fire Alarm Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Fire Alarm Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expert Fire Alarm System Services
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Studerus Electric Inc. specializes in commercial fire alarm system installation, testing, verification, and maintenance. Our ECRA/ESA licensed electricians ensure your fire alarm system meets all Ontario Fire Code requirements and provides reliable protection for your building and occupants.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We work with all major fire alarm manufacturers and provide comprehensive documentation for ESA inspections and insurance requirements.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Code Compliant Installation</h3>
                    <p className="text-gray-600">Full compliance with Ontario Fire Code and building codes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Licensed Electricians</h3>
                    <p className="text-gray-600">ECRA/ESA certified professionals with fire alarm expertise</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Complete Documentation</h3>
                    <p className="text-gray-600">Detailed verification reports and inspection certificates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">24/7 Emergency Service</h3>
                    <p className="text-gray-600">Emergency repairs and system troubleshooting available</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/fy1j25ub_images%20%281%29.jpg"
                alt="Commercial fire alarm installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Our Fire Alarm Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Bell className="h-12 w-12 text-red-600 mb-4" />
                <CardTitle>New System Installation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Complete system design</li>
                  <li>• Control panel installation</li>
                  <li>• Smoke & heat detector placement</li>
                  <li>• Pull station installation</li>
                  <li>• Horn/strobe device installation</li>
                  <li>• System wiring and conduit</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <FileCheck className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Fire Alarm Verification</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Annual verification testing</li>
                  <li>• Device functionality testing</li>
                  <li>• System response verification</li>
                  <li>• Control panel testing</li>
                  <li>• Battery backup testing</li>
                  <li>• Documentation & certification</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>System Upgrades</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Panel replacement/upgrade</li>
                  <li>• Device additions</li>
                  <li>• Code compliance upgrades</li>
                  <li>• Addressable system conversion</li>
                  <li>• Wireless device installation</li>
                  <li>• Integration with building systems</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-yellow-600 mb-4" />
                <CardTitle>Repair & Maintenance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Troubleshooting & diagnostics</li>
                  <li>• Device replacement</li>
                  <li>• Panel repairs</li>
                  <li>• Battery replacement</li>
                  <li>• Preventive maintenance</li>
                  <li>• Emergency repair service</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <AlertTriangle className="h-12 w-12 text-orange-600 mb-4" />
                <CardTitle>Code Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Ontario Fire Code compliance</li>
                  <li>• Building code requirements</li>
                  <li>• ESA inspection coordination</li>
                  <li>• Fire marshal inspections</li>
                  <li>• As-built drawings</li>
                  <li>• Compliance documentation</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Clock className="h-12 w-12 text-purple-600 mb-4" />
                <CardTitle>Emergency Services</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• 24/7 emergency response</li>
                  <li>• System failures</li>
                  <li>• False alarm troubleshooting</li>
                  <li>• Critical repairs</li>
                  <li>• After-hours service</li>
                  <li>• Weekend availability</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Building Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Commercial Buildings We Service
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Office Buildings</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Retail Stores</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Warehouses</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Industrial Facilities</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Apartment Buildings</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Hotels & Hospitality</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Healthcare Facilities</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Educational Buildings</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Restaurants</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Shopping Centers</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Manufacturing</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">Condominiums</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Why Choose Studerus Electric for Fire Alarms?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Licensed & Certified</h3>
              <p className="text-gray-600">ECRA/ESA Licence #7013038 - fully licensed electrical contractors with fire alarm expertise.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Code Compliance</h3>
              <p className="text-gray-600">In-depth knowledge of Ontario Fire Code and building code requirements for fire alarm systems.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Experienced Team</h3>
              <p className="text-gray-600">Team of 17 licensed electricians with commercial fire alarm installation experience.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Complete Documentation</h3>
              <p className="text-gray-600">Thorough verification reports, as-built drawings, and certificates for inspections.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Emergency Response</h3>
              <p className="text-gray-600">24/7 emergency service for system failures and critical repairs.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Insurance Requirements</h3>
              <p className="text-gray-600">We ensure your system meets all insurance company requirements and documentation needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Verification Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            Fire Alarm Verification Process
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Annual verification testing ensures your fire alarm system functions properly and meets code requirements
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-lg mb-2">Visual Inspection</h3>
              <p className="text-gray-600 text-sm">Inspect all devices, panels, and wiring</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-lg mb-2">Device Testing</h3>
              <p className="text-gray-600 text-sm">Test all detectors, pull stations, and notification devices</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-lg mb-2">System Testing</h3>
              <p className="text-gray-600 text-sm">Verify alarm signals, control functions, and battery backup</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-lg mb-2">Documentation</h3>
              <p className="text-gray-600 text-sm">Complete detailed verification report</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                5
              </div>
              <h3 className="font-semibold text-lg mb-2">Certification</h3>
              <p className="text-gray-600 text-sm">Provide official verification certificate</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Fire Alarm Installation or Verification?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact Studerus Electric Inc. today for professional fire alarm system services
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <Link to="/contact">Request Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FireAlarm;
